// Utilitário de exportação PDF e Word para todos os módulos
import logoColorUrl from '../assets/kzero-logo-color.png';
// Marca completa (K + "zeroSystem") em branco: vai sobre a faixa azul da marca,
// igual ao topo dos e-mails. O logo-kzerosystem.png é só o K e não é o logo.
import logoKzeroSystemUrl from '../assets/logo-kzerosystem-wordmark-white.png';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { cnpjFormatado } from './cnpj';

// Converte quebras de linha em <br> para preservar parágrafos em HTML
const nl2br = (text) => (text || '').replace(/\n/g, '<br>');

// Detecta se um anexo é imagem (por data URI ou extensão no nome)
// e devolve um data URI renderizável, corrigindo MIME genérico (octet-stream).
const IMG_EXT = /\.(jpe?g|png|gif|webp|bmp|heic|heif|avif|svg)$/i;
const MIME_POR_EXT = { jpg:'image/jpeg', jpeg:'image/jpeg', png:'image/png', gif:'image/gif', webp:'image/webp', bmp:'image/bmp', heic:'image/heic', heif:'image/heif', avif:'image/avif', svg:'image/svg+xml' };
const resolverImagem = (a) => {
  if (!a || !a.dados || typeof a.dados !== 'string') return null;
  const d = a.dados.trim();
  if (!d || d === '[omitido]') return null;
  // Já é data URI de imagem
  if (d.startsWith('data:image')) return d;
  // URL externa (http/https/blob)
  if (/^https?:\/\//i.test(d) || d.startsWith('blob:')) {
    const m0 = (a.nome || '').match(IMG_EXT);
    return m0 ? d : d;
  }
  // Tenta pela extensão
  const m = (a.nome || '').match(IMG_EXT);
  if (!m) {
    // Último recurso: mesmo sem extensão, se parece base64 puro, tenta jpeg
    if (/^[A-Za-z0-9+/=\s]+$/.test(d) && d.length > 200) {
      return `data:image/jpeg;base64,${d.replace(/\s+/g, '')}`;
    }
    return null;
  }
  const mime = MIME_POR_EXT[m[1].toLowerCase()] || 'image/jpeg';
  // data:<qualquer>;base64,XXX -> data:<mime>;base64,XXX
  const vb = d.indexOf(';base64,');
  if (vb >= 0) return `data:${mime}${d.slice(vb)}`;
  // data:<qualquer>,XXX
  if (d.startsWith('data:')) {
    const v = d.indexOf(',');
    if (v >= 0) return `data:${mime};base64,${d.slice(v + 1)}`;
  }
  // Base64 cru, sem prefixo
  if (/^[A-Za-z0-9+/=\s]+$/.test(d)) {
    return `data:${mime};base64,${d.replace(/\s+/g, '')}`;
  }
  return null;
};

const estilosBase = `
  body { font-family: Arial, sans-serif; padding: 40px; color: #111; font-size: 14px; line-height: 1.6; }
  h1 { font-size: 22px; border-bottom: 2px solid #5b21b6; padding-bottom: 8px; margin-bottom: 20px; color: #3b0764; }
  h2 { font-size: 16px; color: #3b0764; margin-top: 24px; margin-bottom: 8px; border-left: 4px solid #7c3aed; padding-left: 10px; }
  h3 { font-size: 14px; color: #444; margin-top: 16px; }
  p  { margin: 4px 0; }
  .campo { margin-bottom: 10px; }
  .label { font-weight: bold; color: #555; font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; }
  .valor { font-size: 14px; color: #111; white-space: pre-line; }
  .separador { border: none; border-top: 1px solid #ddd; margin: 20px 0; }
  .badge { display: inline-block; padding: 2px 10px; border-radius: 12px; font-size: 12px; font-weight: bold; background: #ede9fe; color: #5b21b6; }
  .rodape { margin-top: 40px; padding-top: 16px; border-top: 2px solid #333; display: flex; justify-content: space-between; }
  .assinatura { text-align: center; min-width: 200px; }
  .linha-assinatura { border-top: 1px solid #333; margin-top: 40px; padding-top: 6px; font-size: 12px; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  th { background: #f3f4f6; padding: 8px; text-align: left; font-size: 12px; border: 1px solid #ddd; }
  td { padding: 8px; border: 1px solid #ddd; font-size: 13px; }
  @media print {
    body { padding: 20px; }
    .no-print { display: none; }
    tr { page-break-inside: avoid; }
    h2, h3 { page-break-after: avoid; }
    .rodape { page-break-inside: avoid; }
  }
`;

const cabecalho = (titulo, subtitulo = '', opts = {}) => {
  if (opts.semCabecalho) return '';
  return `
  <div style="text-align:center;margin-bottom:24px;padding-bottom:16px;border-bottom:2px solid #ede9fe;">
    <img src="${logoColorUrl}" alt="Kzero" style="height:64px;object-fit:contain;display:block;margin:0 auto 12px;" />
    ${opts.ocultarTitulo ? '' : `<h1 style="margin:0 0 4px;font-size:20px;color:#3b0764;">${titulo}</h1>`}
    ${subtitulo ? `<div style="color:#666;font-size:13px;">${subtitulo}</div>` : ''}
    ${opts.ocultarData ? '' : `<div style="font-size:11px;color:#888;margin-top:4px;">Emitido em: ${new Date().toLocaleDateString('pt-BR', {day:'2-digit',month:'long',year:'numeric'})}</div>`}
  </div>
`;
};

export const exportarPDF = (conteudoHTML, titulo, opts = {}) => {
  const janela = window.open('', '_blank');
  if (!janela) { alert('Permita pop-ups para exportar PDF.'); return; }
  // Recibos com layout próprio (semCabecalho) usam padding mínimo para caber em A4 e usar bordas rentes à página.
  const bodyOverride = opts.semCabecalho
    ? 'body { padding: 8mm 10mm; font-size: 11px; line-height: 1.3; }'
    : '';
  janela.document.write(`<!DOCTYPE html><html lang="pt-BR">
    <head><meta charset="UTF-8"><title>${titulo}</title>
    <style>${estilosBase}${bodyOverride}</style></head>
    <body>${cabecalho(titulo, '', opts)}${conteudoHTML}
    <script>window.onload=function(){window.print();}<\/script>
    </body></html>`);
  janela.document.close();
};

export const exportarWord = (conteudoHTML, titulo, opts = {}) => {
  // O Word abre HTML, mas ignora layout moderno (flex, cantos arredondados) e
  // s\u00f3 respeita margem/tamanho de p\u00e1gina via bloco `mso` + regra @page. Sem
  // isto a p\u00e1gina sai sem margem e as tabelas encostam na borda \u2014 parte do que
  // a Bianca via como "todo desconfigurado". Estilos essenciais de tabela
  // repetidos aqui, com apoio mso, para o Word de fato aplicar.
  const estilosWord = `
    @page { size: 21cm 29.7cm; margin: 2cm; mso-page-orientation: portrait; }
    body { font-family: Arial, sans-serif; color:#111; font-size: 12px; line-height: 1.5; }
    h1 { font-size: 20px; color:#3b0764; border-bottom: 2px solid #5b21b6; padding-bottom: 6px; }
    h2 { font-size: 15px; color:#3b0764; }
    table { border-collapse: collapse; width: 100%; mso-table-lspace:0; mso-table-rspace:0; }
    th { background:#f3f4f6; border:1px solid #cccccc; padding:6px; text-align:left; font-size:11px; }
    td { border:1px solid #cccccc; padding:6px; font-size:12px; vertical-align:top; }
    img { max-width: 5cm; height: auto; }
  `;
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office"
    xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
    <head><meta charset="UTF-8"><title>${titulo}</title>
    <!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom><w:DoNotOptimizeForBrowser/></w:WordDocument></xml><![endif]-->
    <style>${estilosBase}${estilosWord}</style></head>
    <body>${cabecalho(titulo, '', opts)}${conteudoHTML}</body></html>`;
  const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${titulo.replace(/[^a-zA-Z0-9À-ú ]/g,'')}.doc`;
  link.click();
  URL.revokeObjectURL(url);
};

// ── Geradores de HTML por módulo ─────────────────────────────────────────────

export const htmlAnotacao = (an, nomeCliente) => `
  <div class="campo"><span class="label">Cliente</span><div class="valor">${nomeCliente || '—'}</div></div>
  <div class="campo"><span class="label">Data</span><div class="valor">${an.data ? new Date(an.data+'T00:00').toLocaleDateString('pt-BR') : '—'}</div></div>
  <div class="campo"><span class="label">Título</span><div class="valor">${an.titulo || '—'}</div></div>
  <div class="campo"><span class="label">Conteúdo</span><div class="valor">${nl2br(an.conteudo) || '—'}</div></div>
`;

export const htmlReembolso = (r, nomeCliente) => `
  <div class="campo"><span class="label">Cliente</span><div class="valor">${nomeCliente || '—'}</div></div>
  <div class="campo"><span class="label">Data</span><div class="valor">${r.data ? new Date(r.data+'T00:00').toLocaleDateString('pt-BR') : '—'}</div></div>
  <div class="campo"><span class="label">Descrição</span><div class="valor">${nl2br(r.descricao) || '—'}</div></div>
  <div class="campo"><span class="label">Valor</span><div class="valor" style="font-size:18px;font-weight:bold;color:#059669;">R$ ${parseFloat(r.valor||0).toLocaleString('pt-BR',{minimumFractionDigits:2, maximumFractionDigits:2})}</div></div>
  <div class="campo"><span class="label">Status</span><div class="valor"><span class="badge">${r.status||'PENDENTE'}</span></div></div>
  ${r.comprovante ? `<div class="campo"><span class="label">Comprovante</span><div class="valor">${r.comprovante}</div></div>` : ''}
  ${(r.anexos||[]).length > 0 ? `
  <div class="campo"><span class="label">Anexos</span>
    <div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:6px;">
      ${(r.anexos||[]).map(a => a.dados && a.dados.startsWith('data:image')
        ? `<img src="${a.dados}" alt="${a.nome}" style="max-width:200px;max-height:160px;border:1px solid #ddd;border-radius:4px;" />`
        : `<span style="font-size:12px;padding:3px 8px;background:#ede9fe;border-radius:4px;color:#5b21b6;">📎 ${a.nome}</span>`
      ).join('')}
    </div>
  </div>` : ''}
  <div class="rodape">
    <div class="assinatura"><div class="linha-assinatura">Solicitante</div></div>
    <div class="assinatura"><div class="linha-assinatura">Aprovador</div></div>
  </div>
`;

export const htmlRelatorioReembolsos = (reembolsos, clientes) => {
  const STATUS_COR = { PENDENTE: '#d97706', PAGO: '#059669', RECUSADO: '#dc2626' };
  const total = reembolsos.reduce((s, r) => s + parseFloat(r.valor || 0), 0);
  const porStatus = Object.fromEntries(['PENDENTE','PAGO','RECUSADO'].map(st => [
    st, reembolsos.filter(r => (r.status||'PENDENTE') === st).reduce((s, r) => s + parseFloat(r.valor||0), 0)
  ]));

  const linhas = reembolsos.map((r, idx) => {
    const cliente = clientes.find(c => String(c.id) === String(r.clienteId));
    const st = r.status || 'PENDENTE';
    const cor = STATUS_COR[st] || '#666';
    const imagens = (r.anexos||[]).filter(a => a.dados && a.dados.startsWith('data:image'));
    const outros  = (r.anexos||[]).filter(a => !a.dados || !a.dados.startsWith('data:image'));

    return `
      <tr style="background:${idx % 2 === 0 ? '#fafafa' : '#fff'};">
        <td style="font-weight:600;color:#3b0764;">${idx + 1}</td>
        <td>${r.data ? new Date(r.data+'T00:00').toLocaleDateString('pt-BR') : '—'}</td>
        <td>${cliente?.nome || '—'}</td>
        <td>${r.descricao || '—'}</td>
        <td style="font-weight:bold;color:#059669;white-space:nowrap;">R$ ${parseFloat(r.valor||0).toLocaleString('pt-BR',{minimumFractionDigits:2, maximumFractionDigits:2})}</td>
        <td><span style="padding:2px 8px;border-radius:10px;font-size:11px;font-weight:bold;background:${cor}22;color:${cor};border:1px solid ${cor};">${st}</span></td>
        <td style="font-size:12px;color:#555;">${r.comprovante || '—'}</td>
      </tr>
      ${(imagens.length > 0 || outros.length > 0) ? `
      <tr style="background:${idx % 2 === 0 ? '#fafafa' : '#fff'};">
        <td colspan="7" style="padding:6px 12px 10px;">
          <div style="font-size:11px;font-weight:bold;color:#888;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:6px;">Anexos</div>
          <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:flex-start;">
            ${imagens.map(a => `<img src="${a.dados}" alt="${a.nome}" style="max-width:160px;max-height:120px;border:1px solid #ddd;border-radius:4px;" />`).join('')}
            ${outros.map(a => `<span style="font-size:12px;padding:3px 10px;background:#ede9fe;border-radius:4px;color:#5b21b6;">📎 ${a.nome}</span>`).join('')}
          </div>
        </td>
      </tr>` : ''}
    `;
  }).join('');

  return `
    <div style="display:flex;gap:16px;flex-wrap:wrap;margin-bottom:20px;">
      ${Object.entries(porStatus).map(([st, val]) => `
        <div style="flex:1;min-width:140px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:12px 16px;">
          <div style="font-size:11px;font-weight:bold;color:#888;text-transform:uppercase;letter-spacing:0.05em;">${st}</div>
          <div style="font-size:18px;font-weight:bold;color:${STATUS_COR[st]};margin-top:4px;">R$ ${val.toLocaleString('pt-BR',{minimumFractionDigits:2, maximumFractionDigits:2})}</div>
          <div style="font-size:12px;color:#999;">${reembolsos.filter(r=>(r.status||'PENDENTE')===st).length} item(ns)</div>
        </div>`).join('')}
      <div style="flex:1;min-width:140px;background:#3b0764;border-radius:8px;padding:12px 16px;">
        <div style="font-size:11px;font-weight:bold;color:#c4b5fd;text-transform:uppercase;letter-spacing:0.05em;">TOTAL GERAL</div>
        <div style="font-size:18px;font-weight:bold;color:#fff;margin-top:4px;">R$ ${total.toLocaleString('pt-BR',{minimumFractionDigits:2, maximumFractionDigits:2})}</div>
        <div style="font-size:12px;color:#c4b5fd;">${reembolsos.length} reembolso(s)</div>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th>#</th><th>Data</th><th>Cliente</th><th>Descrição</th><th>Valor</th><th>Status</th><th>Comprovante</th>
        </tr>
      </thead>
      <tbody>
        ${reembolsos.length === 0 ? '<tr><td colspan="7" style="text-align:center;color:#999;padding:20px;">Nenhum reembolso.</td></tr>' : linhas}
      </tbody>
    </table>

    <div class="rodape">
      <div class="assinatura"><div class="linha-assinatura">Solicitante</div></div>
      <div class="assinatura"><div class="linha-assinatura">Aprovador / Financeiro</div></div>
    </div>
  `;
};

export const htmlAta = (ata, nomeCliente) => {
  const pautas = (ata.pautas || []).map((p, i) => `
    <h3>${i+1}. ${p.titulo || 'Sem título'}</h3>
    ${p.deliberacao ? `<div class="campo"><span class="label">Deliberação</span><div class="valor">${nl2br(p.deliberacao)}</div></div>` : ''}
    ${p.responsaveis ? `<div class="campo"><span class="label">Responsável(is)</span><div class="valor">${nl2br(p.responsaveis)}</div></div>` : ''}
    ${p.prazo ? `<div class="campo"><span class="label">Prazo</span><div class="valor">${new Date(p.prazo+'T00:00').toLocaleDateString('pt-BR')}</div></div>` : ''}
    <hr class="separador"/>
  `).join('');

  const participantesLista = (Array.isArray(ata.participantes) ? ata.participantes : []).filter(Boolean);
  const participantesHtml = participantesLista.length > 0
    ? `<table><thead><tr><th>#</th><th>Nome</th><th>Cargo / Empresa</th></tr></thead><tbody>
        ${participantesLista.map((p, i) => `<tr><td>${i+1}</td><td>${p.nome||'—'}</td><td>${p.cargo||'—'}</td></tr>`).join('')}
       </tbody></table>`
    : '<p style="color:#888;font-size:13px;">Nenhum participante registrado.</p>';

  return `
    <div class="campo"><span class="label">Cliente</span><div class="valor">${nomeCliente || '—'}</div></div>
    <div class="campo"><span class="label">Data</span><div class="valor">${ata.data ? new Date(ata.data+'T00:00').toLocaleDateString('pt-BR') : '—'} das ${ata.hora||'—'} às ${ata.horaFim||'—'}</div></div>
    <div class="campo"><span class="label">Local</span><div class="valor">${ata.local || '—'}</div></div>
    ${ata.redator ? `<div class="campo"><span class="label">Redator</span><div class="valor">${ata.redator}</div></div>` : ''}
    ${ata.emailsDestinatarios ? `<div class="campo"><span class="label">Destinatários</span><div class="valor">${ata.emailsDestinatarios}</div></div>` : ''}
    <h2>Participantes</h2>
    ${participantesHtml}
    <h2>Pautas e Deliberações</h2>
    ${pautas || '<p>Nenhuma pauta registrada.</p>'}
    <div class="rodape">
      <div class="assinatura">
        <div class="linha-assinatura">${ata.redator || 'Redator(a)'}</div>
        <div style="font-size:11px;color:#888;margin-top:4px;">Redator(a)</div>
      </div>
      <div class="assinatura">
        <div class="linha-assinatura">Aprovado por</div>
      </div>
    </div>
  `;
};

// Gera o PDF como Blob a partir do HTML do documento (para enviar por email ou download)
export const gerarPDFBlob = async (conteudoHTML, titulo) => {
  const container = document.createElement('div');
  container.style.cssText = 'position:fixed;left:-9999px;top:0;width:794px;background:#fff;padding:40px;font-family:Arial,sans-serif;font-size:14px;line-height:1.6;color:#111;';
  container.innerHTML = `<style>${estilosBase.replace(/body\s*\{[^}]*\}/g, '')}</style>${conteudoHTML}`;
  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, { scale: 2, useCORS: true, logging: false });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const ratio = canvas.width / canvas.height;
    const imgH  = pageW / ratio;
    let posY = 0;

    if (imgH <= pageH) {
      pdf.addImage(imgData, 'PNG', 0, 0, pageW, imgH);
    } else {
      // Múltiplas páginas
      let remaining = canvas.height;
      let srcY = 0;
      while (remaining > 0) {
        const sliceH = Math.min(remaining, Math.round(canvas.width * pageH / pageW));
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width  = canvas.width;
        sliceCanvas.height = sliceH;
        sliceCanvas.getContext('2d').drawImage(canvas, 0, srcY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
        const sliceData = sliceCanvas.toDataURL('image/png');
        if (posY > 0) pdf.addPage();
        pdf.addImage(sliceData, 'PNG', 0, 0, pageW, pageH);
        srcY      += sliceH;
        remaining -= sliceH;
        posY++;
      }
    }

    return pdf.output('blob');
  } finally {
    document.body.removeChild(container);
  }
};

export const htmlNotaDebito = (reembolsos, clientes, numeroOverride) => {
  const numero = numeroOverride || `ND-${new Date().getFullYear()}-${String(Date.now()).slice(-5)}`;
  const dataEmissao = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  const total = reembolsos.reduce((s, r) => s + parseFloat(r.valor || 0), 0);
  const todosVencimentos = reembolsos.map(r => r.vencimento).filter(Boolean);
  const vencimentoGeral = todosVencimentos.length > 0 ? todosVencimentos.sort().at(-1) : null;
  // Meio de pagamento e dados — pega do primeiro item que tiver preenchido
  const meioPagamento = reembolsos.find(r => r.meioPagamento)?.meioPagamento || null;
  const dadosPagamento = reembolsos.find(r => r.dadosPagamento)?.dadosPagamento || null;
  // Observações — pega a primeira não-vazia (é o mesmo valor para todos os itens do modal)
  const observacoesGerais = reembolsos.find(r => r.observacoes)?.observacoes || '';

  // Agrupa por cliente
  const porCliente = {};
  reembolsos.forEach(r => {
    const cId = String(r.clienteId || r.cliente || '');
    if (!porCliente[cId]) porCliente[cId] = [];
    porCliente[cId].push(r);
  });

  const blocos = Object.entries(porCliente).map(([cId, itens]) => {
    const cliente = clientes.find(c => String(c.id) === cId);
    const subtotal = itens.reduce((s, r) => s + parseFloat(r.valor || 0), 0);

    const vencimentos = itens.map(r => r.vencimento).filter(Boolean);
    const vencimentoMax = vencimentos.length > 0 ? vencimentos.sort().at(-1) : null;

    const linhas = itens.map((r, idx) => {
      const anexosResolvidos = (r.anexos || []).map(a => ({ a, src: resolverImagem(a) }));
      const imgs = anexosResolvidos.filter(x => x.src).map(({ a, src }) =>
        `<img src="${src}" alt="${a.nome||'anexo'}" style="max-width:160px;max-height:120px;border:1px solid #ddd;border-radius:4px;margin:4px 4px 0 0;" />`
      ).join('');
      const docs = anexosResolvidos.filter(x => !x.src).map(({ a }) =>
        `<div style="font-size:11px;color:#555;margin-top:2px;">📎 ${a.nome}</div>`
      ).join('');
      const temAnexo = (r.anexos || []).length > 0;
      const anexoCell = imgs || docs
        ? `<div style="margin-top:4px;">${imgs}${docs}<div style="font-size:10px;color:#7c3aed;margin-top:4px;font-weight:600;">Ver comprovante em tamanho real ao final</div></div>`
        : (temAnexo ? '<span style="font-size:10px;color:#7c3aed;">ver ao final</span>' : '');
      return `
        <tr style="background:${idx % 2 === 0 ? '#fafafa' : '#fff'};page-break-inside:avoid;">
          <td style="font-weight:600;color:#3b0764;">${idx + 1}</td>
          <td>${r.data ? new Date(r.data + 'T00:00').toLocaleDateString('pt-BR') : '—'}</td>
          <td style="font-size:12px;">${r.categoria || '—'}</td>
          <td style="font-size:12px;color:#555;">${r.projeto || '—'}</td>
          <td>${r.descricao || '—'}${r.comprovante ? `<div style="font-size:11px;color:#888;margin-top:2px;">${r.comprovante}</div>` : ''}</td>
          <td>${anexoCell || '—'}</td>
          <td style="font-weight:bold;color:#059669;white-space:nowrap;text-align:right;">R$ ${parseFloat(r.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
      `;
    }).join('');

    return `
      <div style="margin-bottom:32px;page-break-inside:avoid;">
        <h2 style="margin-bottom:4px;page-break-after:avoid;">Cliente: ${cliente?.nome || 'Não identificado'}</h2>
        ${cnpjFormatado(cliente?.cnpj) ? `<p style="font-size:12px;color:#666;margin:0 0 4px;">CNPJ: ${cnpjFormatado(cliente.cnpj)}</p>` : ''}
        ${cliente?.email ? `<p style="font-size:12px;color:#666;margin:0 0 12px;">${cliente.email}</p>` : ''}
        ${vencimentoMax ? `<p style="font-size:12px;color:#dc2626;margin:0 0 12px;font-weight:bold;">⚠ Vencimento: ${new Date(vencimentoMax+'T00:00').toLocaleDateString('pt-BR')}</p>` : ''}
        <table style="page-break-inside:auto;">
          <thead>
            <tr>
              <th style="width:32px;">#</th>
              <th>Data</th>
              <th>Categoria</th>
              <th>Projeto / Ref.</th>
              <th>Descrição / Comprovante</th>
              <th>Anexos</th>
              <th style="text-align:right;">Valor</th>
            </tr>
          </thead>
          <tbody>${linhas}</tbody>
          <tfoot>
            <tr style="background:#3b0764;page-break-inside:avoid;">
              <td colspan="6" style="font-weight:bold;color:#ede9fe;text-align:right;padding:10px 8px;border-color:#5b21b6;">Subtotal ${cliente?.nome || ''}:</td>
              <td style="font-weight:bold;color:#fff;font-size:16px;text-align:right;padding:10px 8px;border-color:#5b21b6;white-space:nowrap;">R$ ${subtotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    `;
  }).join('');

  // Seção de comprovantes em tamanho real — permite ao destinatário validar cada despesa
  const comprovantesBlocos = Object.entries(porCliente).map(([cId, itens]) => {
    const cliente = clientes.find(c => String(c.id) === cId);
    const itensComAnexo = itens
      .map((r, idx) => ({ r, idx }))
      .filter(({ r }) => (r.anexos || []).length > 0);
    if (itensComAnexo.length === 0) return '';

    const cards = itensComAnexo.map(({ r, idx }) => {
      const anexosResolvidos = (r.anexos || []).map(a => {
        const src = resolverImagem(a);
        if (!src && a && typeof a.dados === 'string') {
          console.warn('[NotaDebito] anexo sem imagem renderizavel', { nome: a.nome, len: a.dados.length, prefixo: a.dados.slice(0, 60) });
        }
        return { a, src };
      });
      const imgs = anexosResolvidos.filter(x => x.src).map(({ a, src }) => `
          <figure style="margin:8px 0;page-break-inside:avoid;">
            <img src="${src}" alt="${a.nome || 'comprovante'}"
                 style="max-width:100%;max-height:520px;border:1px solid #d1d5db;border-radius:6px;display:block;" />
            <figcaption style="font-size:11px;color:#6b7280;margin-top:4px;">${a.nome || 'comprovante'}</figcaption>
          </figure>
        `).join('');
      const docs = anexosResolvidos.filter(x => !x.src).map(({ a }) =>
        `<li style="font-size:12px;color:#374151;">📎 ${a.nome || 'arquivo'} <span style="color:#9ca3af;">(arquivo não-imagem — solicite o original)</span></li>`
      ).join('');
      const docsBlock = docs ? `<ul style="margin:8px 0 0 18px;padding:0;">${docs}</ul>` : '';
      const valor = `R$ ${parseFloat(r.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const dataStr = r.data ? new Date(r.data + 'T00:00').toLocaleDateString('pt-BR') : '—';
      return `
        <div style="border:1px solid #e5e7eb;border-radius:8px;padding:12px 16px;margin-bottom:16px;page-break-inside:avoid;">
          <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px;margin-bottom:6px;flex-wrap:wrap;">
            <div style="font-weight:bold;color:#3b0764;font-size:13px;">Item ${idx + 1} — ${dataStr}</div>
            <div style="font-weight:bold;color:#059669;font-size:13px;">${valor}</div>
          </div>
          <div style="font-size:12px;color:#374151;margin-bottom:4px;">${r.descricao || '—'}</div>
          ${r.categoria || r.projeto ? `<div style="font-size:11px;color:#6b7280;margin-bottom:8px;">${[r.categoria, r.projeto].filter(Boolean).join(' · ')}</div>` : ''}
          ${imgs}
          ${docsBlock}
        </div>
      `;
    }).join('');

    return `
      <div style="margin-bottom:24px;page-break-inside:auto;">
        <h2 style="page-break-after:avoid;">Comprovantes — ${cliente?.nome || 'Não identificado'}</h2>
        ${cards}
      </div>
    `;
  }).join('');

  const comprovantesSection = comprovantesBlocos ? `
    <div style="page-break-before:always;"></div>
    <div style="background:#ede9fe;border-left:4px solid #7c3aed;padding:12px 16px;border-radius:0 8px 8px 0;margin-bottom:20px;">
      <p style="margin:0;font-size:13px;color:#3b0764;">
        <strong>Anexos para validação.</strong> As imagens abaixo correspondem aos comprovantes originais de cada despesa relacionada nesta Nota de Débito.
      </p>
    </div>
    ${comprovantesBlocos}
  ` : '';

  const totalGeralBlock = Object.keys(porCliente).length > 1 ? `
    <div style="background:#1e1b4b;border-radius:8px;padding:16px 20px;display:flex;justify-content:space-between;align-items:center;margin-bottom:24px;">
      <span style="color:#c4b5fd;font-weight:bold;font-size:14px;text-transform:uppercase;letter-spacing:0.05em;">Total Geral a Receber</span>
      <span style="color:#fff;font-weight:bold;font-size:22px;">R$ ${total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
    </div>
  ` : '';

  return `
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding:16px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;">
      <div>
        <div style="font-size:11px;font-weight:bold;color:#888;text-transform:uppercase;letter-spacing:0.05em;">Número da Nota</div>
        <div style="font-size:18px;font-weight:bold;color:#3b0764;">${numero}</div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:11px;font-weight:bold;color:#888;text-transform:uppercase;letter-spacing:0.05em;">Data de Emissão</div>
        <div style="font-size:14px;color:#111;">${dataEmissao}</div>
      </div>
    </div>

    <div style="background:#ede9fe;border-left:4px solid #7c3aed;padding:12px 16px;border-radius:0 8px 8px 0;margin-bottom:24px;">
      <p style="margin:0;font-size:13px;color:#3b0764;">
        Esta Nota de Débito relaciona as despesas incorridas pela <strong>Kzero Consultoria</strong> em nome do(s) cliente(s) abaixo,
        as quais devem ser reembolsadas conforme os comprovantes apresentados.
      </p>
    </div>

    ${blocos}
    ${totalGeralBlock}

    <div style="border:1px solid #e5e7eb;border-radius:8px;padding:16px;margin-bottom:24px;page-break-inside:avoid;">
      <div style="font-size:11px;font-weight:bold;color:#888;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:12px;">Instruções de Pagamento</div>
      <div style="display:flex;gap:24px;flex-wrap:wrap;">
        ${vencimentoGeral ? `
        <div>
          <div style="font-size:11px;color:#888;font-weight:bold;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:3px;">Prazo para Pagamento</div>
          <div style="font-size:14px;color:#dc2626;font-weight:bold;">${new Date(vencimentoGeral+'T00:00').toLocaleDateString('pt-BR', {day:'2-digit',month:'long',year:'numeric'})}</div>
        </div>` : ''}
        ${meioPagamento ? `
        <div>
          <div style="font-size:11px;color:#888;font-weight:bold;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:3px;">Meio de Pagamento</div>
          <div style="font-size:14px;color:#111;font-weight:600;">${meioPagamento}</div>
        </div>` : ''}
      </div>
      ${dadosPagamento ? `
      <div style="margin-top:10px;background:#f9fafb;border-radius:6px;padding:10px 14px;">
        <div style="font-size:11px;color:#888;font-weight:bold;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:3px;">Dados para Pagamento</div>
        <div style="font-size:13px;color:#111;">${nl2br(dadosPagamento)}</div>
      </div>` : ''}
      ${!vencimentoGeral && !meioPagamento ? `
      <p style="margin:0;font-size:13px;color:#444;">O pagamento deve ser efetuado conforme acordado em contrato. Em caso de dúvidas, entre em contato com a Kzero Consultoria.</p>` : ''}
    </div>

    ${observacoesGerais ? `
    <div style="border:1px solid #fde68a;background:#fffbeb;border-radius:8px;padding:14px 16px;margin-bottom:24px;page-break-inside:avoid;">
      <div style="font-size:11px;font-weight:bold;color:#92400e;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:6px;">Observações</div>
      <p style="margin:0;font-size:13px;color:#78350f;">${nl2br(observacoesGerais)}</p>
    </div>` : ''}

    <div class="rodape">
      <div class="assinatura">
        <div class="linha-assinatura">Kzero Consultoria</div>
        <div style="font-size:11px;color:#888;margin-top:4px;">Emitente</div>
      </div>
      <div class="assinatura">
        <div class="linha-assinatura">Cliente / Aprovador</div>
        <div style="font-size:11px;color:#888;margin-top:4px;">Ciente e de acordo</div>
      </div>
    </div>

    ${comprovantesSection}
  `;
};

const fmtBR = (v) => `R$ ${parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MESES_EXTENSO = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];

export const htmlReciboProLabore = (r) => {
  // Layout tradicional de recibo de folha (padrão Domínio/Alterdata).
  // Uma ÚNICA tabela com 20 colunas base e colspan por seção — garante alinhamento
  // vertical e bordas contínuas idênticas ao modelo.
  const bruto  = parseFloat(r.valorBruto || 0);
  const inss   = parseFloat(r.inss       || 0);
  const irrf   = parseFloat(r.irrf       || 0);
  const outros = parseFloat(r.outros     || 0);
  const totalVenc = bruto;
  const totalDesc = inss + irrf + outros;
  const liquido = r.valorLiquido !== '' && r.valorLiquido != null
    ? parseFloat(r.valorLiquido)
    : (totalVenc - totalDesc);
  const baseIR = Math.max(0, bruto - inss);

  let compMMAAAA = '';
  if (r.competencia && /^\d{4}-\d{2}$/.test(r.competencia)) {
    const [ano, mes] = r.competencia.split('-');
    compMMAAAA = `${mes}/${ano}`;
  }

  const enderecoLinha = r.empresaEndereco || [
    [r.empresaLogradouro, r.empresaNumero].filter(Boolean).join(', '),
    r.empresaComplemento,
    r.empresaBairro,
    [r.empresaCidade, r.empresaUF].filter(Boolean).join('/'),
    r.empresaCEP ? `CEP ${r.empresaCEP}` : '',
  ].filter(Boolean).join(' — ');

  const numeroReg = r.numeroRegistro || '001';
  const fmt = (v) => (parseFloat(v || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const verbas = [
    { cod: '001', desc: 'PRO-LABORE',       ref: '',   venc: bruto,  descV: 0 },
    inss   > 0 ? { cod: '036', desc: 'INSS',              ref: '11,00', venc: 0, descV: inss }   : null,
    irrf   > 0 ? { cod: '038', desc: 'IRRF',              ref: '27,50', venc: 0, descV: irrf }   : null,
    outros > 0 ? { cod: '039', desc: 'OUTROS DESCONTOS',  ref: '',      venc: 0, descV: outros } : null,
  ].filter(Boolean);

  const linhaVerba = (v) => `
    <tr class="pl-verba">
      <td colspan="2" class="pl-c">${v.cod}</td>
      <td colspan="9">${v.desc}</td>
      <td colspan="3" class="pl-r">${v.ref}</td>
      <td colspan="3" class="pl-r">${v.venc  > 0 ? fmt(v.venc)  : ''}</td>
      <td colspan="3" class="pl-r">${v.descV > 0 ? fmt(v.descV) : ''}</td>
    </tr>`;

  const linhaVazia = `
    <tr class="pl-vazia">
      <td colspan="2">&nbsp;</td><td colspan="9"></td><td colspan="3"></td><td colspan="3"></td><td colspan="3"></td>
    </tr>`;
  const vazias = Array.from({ length: Math.max(0, 15 - verbas.length) }).map(() => linhaVazia).join('');

  return `
    <style>
      @page { size: A4 portrait; margin: 10mm; }
      .pl-wrap { font-family: Arial, Helvetica, sans-serif; color: #000; }
      .pl-wrap table.pl-main { width: 100%; border-collapse: collapse; table-layout: fixed; margin: 0 !important; }
      .pl-wrap table.pl-main td,
      .pl-wrap table.pl-main th {
        border: 1px solid #000 !important;
        padding: 2px 4px !important;
        font-size: 9px !important;
        font-weight: normal !important;
        text-align: left !important;
        vertical-align: top !important;
        background: #fff !important;
        color: #000 !important;
        line-height: 1.25 !important;
        letter-spacing: 0 !important;
      }
      .pl-wrap .pl-lbl { display: block; font-size: 7.5px; color: #333; text-transform: uppercase; letter-spacing: 0.02em; }
      .pl-wrap .pl-val { display: block; font-size: 10px; padding-top: 1px; }
      .pl-wrap .pl-val.bold { font-weight: bold; }
      .pl-wrap .pl-r { text-align: right !important; }
      .pl-wrap .pl-c { text-align: center !important; }
      .pl-wrap .pl-title { text-align: center !important; font-weight: bold !important; font-size: 11px !important; padding: 6px 4px !important; }
      .pl-wrap .pl-head { background: #e8e8e8 !important; font-weight: bold !important; }
      .pl-wrap .pl-verba td { height: 14px; }
      .pl-wrap .pl-vazia td { height: 14px; }
      .pl-wrap .pl-liquido { font-weight: bold !important; font-size: 12px !important; text-align: right !important; }
      .pl-wrap .pl-decl { border: 1px solid #000; padding: 6px 8px; margin-top: 10px; min-height: 65px; font-size: 10px; }
      .pl-wrap .pl-sig { border-top: 1px solid #000; margin-top: 40px; padding-top: 2px; font-size: 8px; color: #333; text-align: center; }
      .pl-wrap .pl-rodape { display: flex; justify-content: space-between; font-size: 8px; color: #333; margin-top: 4px; padding: 0 2px; }
    </style>
    <div class="pl-wrap">
      <table class="pl-main">
        <colgroup>
          ${Array.from({ length: 20 }).map(() => '<col style="width:5%">').join('')}
        </colgroup>

        <!-- LINHA 1: TÍTULO + COMPETÊNCIA -->
        <tr>
          <td colspan="15" class="pl-title">RECIBO DE PAGAMENTO DE PRÓ-LABORE</td>
          <td colspan="5" style="text-align:center !important;">
            <span class="pl-lbl">Competência</span>
            <span class="pl-val bold" style="text-align:center;">${compMMAAAA || '&nbsp;'}</span>
          </td>
        </tr>

        <!-- LINHA 2: EMPRESA (razão + CNPJ + endereço) + DIVISÃO RH -->
        <tr>
          <td colspan="15">
            <span class="pl-lbl">Empresa</span>
            <span class="pl-val bold">${(r.empresaRazaoSocial || '').toUpperCase() || '&nbsp;'}</span>
            ${r.empresaCNPJ ? `<span class="pl-val" style="font-size:9px;"><strong>CNPJ:</strong> ${r.empresaCNPJ}</span>` : ''}
            <span class="pl-val" style="font-size:9px;">${enderecoLinha || '&nbsp;'}</span>
          </td>
          <td colspan="5">
            <span class="pl-lbl">Divisão R.H.</span>
            <span class="pl-val">&nbsp;</span>
          </td>
        </tr>

        <!-- LINHA 3: Nº REG + NOME + FUNÇÃO -->
        <tr>
          <td colspan="2" class="pl-c">
            <span class="pl-lbl" style="text-align:center;">Nº Reg.</span>
            <span class="pl-val" style="text-align:center;">${numeroReg}</span>
          </td>
          <td colspan="13">
            <span class="pl-lbl">Nome</span>
            <span class="pl-val">${(r.socioNome || '').toUpperCase() || '&nbsp;'}${r.socioCPF ? `&nbsp;&nbsp;·&nbsp;&nbsp;CPF ${r.socioCPF}` : ''}</span>
          </td>
          <td colspan="5">
            <span class="pl-lbl">Função</span>
            <span class="pl-val" style="text-transform:uppercase;">${r.socioFuncao || 'SÓCIO'}</span>
          </td>
        </tr>

        <!-- LINHA 4: CABEÇALHO DA TABELA DE VERBAS -->
        <tr class="pl-head">
          <td colspan="2" class="pl-c">Cód.</td>
          <td colspan="9">Descrição</td>
          <td colspan="3" class="pl-r">Referência</td>
          <td colspan="3" class="pl-r">Vencimentos</td>
          <td colspan="3" class="pl-r">Descontos</td>
        </tr>

        ${verbas.map(linhaVerba).join('')}
        ${vazias}

        <!-- CABEÇALHO RESUMO 1 -->
        <tr class="pl-head">
          <td colspan="3">RESUMO</td>
          <td colspan="4">Salário Base</td>
          <td colspan="4">Sal. Contribuição</td>
          <td colspan="4" class="pl-r">Total de Vencimentos</td>
          <td colspan="5" class="pl-r">Total de Descontos</td>
        </tr>
        <!-- VALORES RESUMO 1 -->
        <tr>
          <td colspan="3">&nbsp;</td>
          <td colspan="4" class="pl-r">${fmt(bruto)}</td>
          <td colspan="4" class="pl-r">${fmt(bruto)}</td>
          <td colspan="4" class="pl-r" style="font-weight:bold !important;">${fmt(totalVenc)}</td>
          <td colspan="5" class="pl-r" style="font-weight:bold !important;">${fmt(totalDesc)}</td>
        </tr>
        <!-- CABEÇALHO RESUMO 2 -->
        <tr class="pl-head">
          <td colspan="3">&nbsp;</td>
          <td colspan="4">Base Cál. F.G.T.S.</td>
          <td colspan="4">F.G.T.S. do Mês</td>
          <td colspan="4" class="pl-r">Base Cál. I.R.<br><span style="font-weight:normal;font-size:9px;">${fmt(baseIR)}</span></td>
          <td colspan="5" class="pl-r" style="font-size:11px !important;">L Í Q U I D O<br><span style="font-weight:normal;font-size:8px;">A  R E C E B E R</span></td>
        </tr>
        <!-- VALORES RESUMO 2 -->
        <tr>
          <td colspan="3" style="font-size:9px !important;">PRO-LABORE ${compMMAAAA || ''}</td>
          <td colspan="4" class="pl-r">0,00</td>
          <td colspan="4" class="pl-r">0,00</td>
          <td colspan="4" class="pl-r">&nbsp;</td>
          <td colspan="5" class="pl-liquido">${fmt(liquido)}</td>
        </tr>
      </table>

      <div class="pl-decl">
        DECLARO TER RECEBIDO A IMPORTÂNCIA LÍQUIDA DISCRIMINADA NESTE RECIBO
        <div class="pl-sig">DATA / ASSINATURA</div>
      </div>

      <div class="pl-rodape">
        <span>PRO-LABORE ${compMMAAAA || ''}</span>
        <span>2ª via / Empresa</span>
      </div>
    </div>
  `;
};

// Proposta guarda o cliente pelo nome (select por c.nome), não pelo id.
export const htmlProposta = (p, clientes = []) => {
  const cnpjCliente = cnpjFormatado((clientes || []).find(c => c?.nome && c.nome === p.cliente)?.cnpj);
  const fmtData = (d) => d ? new Date(d+'T00:00').toLocaleDateString('pt-BR',{day:'2-digit',month:'long',year:'numeric'}) : '—';
  const clausulasAtivas = (p.clausulas || []).filter(c => c.ativa);

  return `
    <div class="campo"><span class="label">Cliente</span><div class="valor">${p.cliente || '—'}</div></div>
    ${cnpjCliente ? `<div class="campo"><span class="label">CNPJ</span><div class="valor">${cnpjCliente}</div></div>` : ''}
    ${p.responsavel ? `<div class="campo"><span class="label">Responsável</span><div class="valor">${p.responsavel}</div></div>` : ''}
    <div class="campo"><span class="label">Data / Validade</span><div class="valor">${fmtData(p.data)} — válida até ${fmtData(p.validade)}</div></div>
    ${p.objetivo    ? `<h2>1. Objetivo</h2><p>${nl2br(p.objetivo)}</p>` : ''}
    ${p.escopo      ? `<h2>2. Escopo dos Serviços</h2><p>${nl2br(p.escopo)}</p>` : ''}
    ${p.entregas    ? `<h2>3. Entregas Previstas</h2><p>${nl2br(p.entregas)}</p>` : ''}
    ${p.metodologia ? `<h2>4. Metodologia</h2><p>${nl2br(p.metodologia)}</p>` : ''}
    ${p.cronograma  ? `<h2>5. Cronograma</h2><p>${nl2br(p.cronograma)}</p>` : ''}
    <h2>${p.objetivo ? '6' : '1'}. Investimento</h2>
    <div class="campo"><span class="label">Valor Total</span><div class="valor" style="font-size:20px;font-weight:bold;color:#059669;">${p.valor ? `R$ ${parseFloat(p.valor).toLocaleString('pt-BR',{minimumFractionDigits:2, maximumFractionDigits:2})}` : 'A definir'}</div></div>
    ${p.formaPagamento ? `<div class="campo"><span class="label">Forma de Pagamento</span><div class="valor">${nl2br(p.formaPagamento)}</div></div>` : ''}
    ${p.condicoesEspeciais ? `<div class="campo"><span class="label">Condições Especiais</span><div class="valor">${nl2br(p.condicoesEspeciais)}</div></div>` : ''}
    ${clausulasAtivas.length > 0 ? `
      <h2>Cláusulas Contratuais</h2>
      ${clausulasAtivas.map(c => `<h3>${c.titulo}</h3><p>${nl2br(c.texto)}</p>`).join('')}
    ` : ''}
    ${p.observacoes ? `<h2>Observações</h2><p>${nl2br(p.observacoes)}</p>` : ''}
    <div class="rodape">
      <div class="assinatura"><div class="linha-assinatura">Kzero Consultoria</div></div>
      <div class="assinatura"><div class="linha-assinatura">Cliente: ${p.cliente || '_______________'}</div>${cnpjCliente ? `<div style="font-size:11px;color:#888;margin-top:4px;">CNPJ ${cnpjCliente}</div>` : ''}</div>
    </div>
  `;
};

// ── Orçamento por módulos+submódulos do KzeroSystem ─────────────────────────
// Gera HTML de proposta comercial de contratação do SaaS: grupos por módulo,
// linhas de submódulo com preço unitário × nº empresas × desconto% cada.
// Total geral = soma de todos os submódulos selecionados.
export const htmlOrcamento = (orc, cliente) => {
  const fmtMoeda = (v) => `R$ ${parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtData = (d) => d ? new Date(d + 'T00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';
  const numEmpresas = parseInt(orc.numEmpresas, 10) || 1;

  let total = 0;
  let totalSubmodulos = 0;

  // Módulo entra no orçamento se for vendido inteiro (preço fechado) ou se tiver
  // ao menos 1 submódulo selecionado. No modo inteiro, TODOS os submódulos vão
  // listados como inclusos, sem preço individual.
  const modulosComSelecao = (orc.modulos || []).map(mod => {
    const inteiro = mod.modoVenda === 'inteiro';
    const subsSel = inteiro ? (mod.submodulos || []) : (mod.submodulos || []).filter(s => s.selecionado);
    return { ...mod, inteiro, submodulosSelecionados: subsSel };
  }).filter(m => m.inteiro || m.submodulosSelecionados.length > 0);

  const grupos = modulosComSelecao.map(mod => {
    let subtotalMod = 0;
    let linhas = '';

    if (mod.inteiro) {
      const valorMod = parseFloat(mod.valorModulo) || 0;
      const descMod = Math.min(100, Math.max(0, parseFloat(mod.descontoModuloPct) || 0));
      subtotalMod = valorMod * numEmpresas * (1 - descMod / 100);
      totalSubmodulos += mod.submodulosSelecionados.length;

      linhas = mod.submodulosSelecionados.map((sub, idx) => `
        <tr style="background:${idx % 2 === 0 ? '#fafafa' : '#fff'};page-break-inside:avoid;">
          <td style="padding-left:24px;font-size:12px;color:#111;">${sub.label}</td>
          <td colspan="3" style="text-align:right;font-size:11px;color:#059669;font-style:italic;">incluso no módulo</td>
        </tr>
      `).join('');
    } else {
      linhas = mod.submodulosSelecionados.map((sub, idx) => {
        const valor = parseFloat(sub.valorUnitario) || 0;
        const desc = Math.min(100, Math.max(0, parseFloat(sub.descontoPct) || 0));
        const subtotal = valor * numEmpresas * (1 - desc / 100);
        subtotalMod += subtotal;
        totalSubmodulos += 1;

        return `
        <tr style="background:${idx % 2 === 0 ? '#fafafa' : '#fff'};page-break-inside:avoid;">
          <td style="padding-left:24px;font-size:12px;color:#111;">${sub.label}</td>
          <td style="text-align:right;white-space:nowrap;font-size:12px;">${valor > 0 ? fmtMoeda(valor) : '<span style="color:#dc2626;font-size:11px;">sem valor</span>'}</td>
          <td style="text-align:right;font-size:12px;">${desc > 0 ? `<span style="color:#059669;font-weight:600;">${desc.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%</span>` : '—'}</td>
          <td style="text-align:right;font-weight:600;color:#059669;white-space:nowrap;font-size:12px;">${fmtMoeda(subtotal)}</td>
        </tr>
      `;
      }).join('');
    }
    total += subtotalMod;

    const descModPct = Math.min(100, Math.max(0, parseFloat(mod.descontoModuloPct) || 0));
    const selo = mod.inteiro
      ? `<span style="display:inline-block;margin-left:8px;padding:1px 7px;border-radius:999px;background:#7c3aed;color:#fff;font-size:9px;font-weight:bold;text-transform:uppercase;letter-spacing:0.04em;vertical-align:middle;">Módulo completo${descModPct > 0 ? ` · -${descModPct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%` : ''}</span>`
      : '';

    // Sem a descrição do catálogo embaixo do módulo: ela lista tudo o que o
    // módulo tem e dava a entender que estava sendo contratado (Bianca,
    // 21/10/2026). O que entra no orçamento são as linhas de submódulo.
    return `
      <tr style="background:#ede9fe;page-break-inside:avoid;">
        <td colspan="3" style="font-weight:bold;color:#3b0764;padding:8px;border-color:#c4b5fd;font-size:13px;">
          ${mod.label}${selo}
        </td>
        <td style="text-align:right;font-weight:bold;color:#3b0764;padding:8px;border-color:#c4b5fd;white-space:nowrap;font-size:13px;">${fmtMoeda(subtotalMod)}</td>
      </tr>
      ${linhas}
    `;
  }).join('');

  // Itens "Outros": descrição + valor livres, com flag mensal (entra no total
  // mensal, x12 no anual) ou avulso (só no anual, uma vez).
  // Linha com descrição entra na proposta mesmo sem valor — são os itens
  // "conforme plano" (armazenamento incluso, cota de chamados do assistente
  // de IA). O cliente precisa enxergar o que está contratando; só não somam
  // no total, o que já é garantido abaixo pelo `v <= 0`.
  const itensExtras = (orc.itensExtras || []).filter(it =>
    (parseFloat(it.valor) || 0) > 0 || String(it.descricao || '').trim() !== ''
  );
  let totalExtrasMensal = 0;
  let totalExtrasAvulso = 0;
  for (const it of itensExtras) {
    const v = parseFloat(it.valor) || 0;
    if (it.mensal) totalExtrasMensal += v;
    else totalExtrasAvulso += v;
  }
  const totalMensal = total + totalExtrasMensal;
  const totalAnual = totalMensal * 12 + totalExtrasAvulso;
  const totalPorEmpresa = numEmpresas > 0 ? totalMensal / numEmpresas : totalMensal;
  const numero = orc.numero || `ORC-${new Date().getFullYear()}-${String(Date.now()).slice(-5)}`;

  const htmlExtras = itensExtras.length === 0 ? '' : `
    <h2>Outros</h2>
    <table>
      <thead>
        <tr>
          <th>Descrição</th>
          <th style="text-align:center;width:90px;">Recorrência</th>
          <th style="text-align:right;width:130px;">Valor</th>
        </tr>
      </thead>
      <tbody>
        ${itensExtras.map((it, idx) => `
          <tr style="background:${idx % 2 === 0 ? '#fafafa' : '#fff'};page-break-inside:avoid;">
            <td style="padding:6px 8px;font-size:12px;color:#111;">${it.descricao || '(sem descrição)'}</td>
            <td style="text-align:center;font-size:11px;color:${it.mensal ? '#059669' : '#7c3aed'};">${(parseFloat(it.valor) || 0) > 0 ? (it.mensal ? 'Mensal' : 'Avulso') : '—'}</td>
            <td style="text-align:right;font-weight:600;color:#059669;white-space:nowrap;font-size:12px;padding:6px 8px;">${(parseFloat(it.valor) || 0) > 0 ? fmtMoeda(parseFloat(it.valor) || 0) : '<span style="color:#6b7280;font-style:italic;font-weight:400;">Incluso</span>'}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>`;

  return `
    <div style="text-align:center;margin-bottom:24px;padding:20px 16px 14px;background:#12306E;border-radius:8px;-webkit-print-color-adjust:exact;print-color-adjust:exact;">
      <img src="${logoKzeroSystemUrl}" alt="KzeroSystem" style="height:44px;object-fit:contain;display:block;margin:0 auto 10px;" />
      <div style="font-size:11px;color:#dbe3f5;">Sistema de Gestão da Qualidade · kzerosystem.com.br</div>
    </div>

    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding:16px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;">
      <div>
        <div style="font-size:11px;font-weight:bold;color:#888;text-transform:uppercase;letter-spacing:0.05em;">Nº do Orçamento</div>
        <div style="font-size:18px;font-weight:bold;color:#3b0764;">${numero}</div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:11px;font-weight:bold;color:#888;text-transform:uppercase;letter-spacing:0.05em;">Emitido em</div>
        <div style="font-size:14px;color:#111;">${fmtData(orc.data)}</div>
        ${orc.validade ? `<div style="font-size:11px;color:#dc2626;margin-top:4px;"><strong>Válido até:</strong> ${fmtData(orc.validade)}</div>` : ''}
      </div>
    </div>

    <div style="background:#ede9fe;border-left:4px solid #7c3aed;padding:12px 16px;border-radius:0 8px 8px 0;margin-bottom:24px;">
      <p style="margin:0;font-size:13px;color:#3b0764;">
        Proposta de contratação da plataforma <strong>KzeroSystem</strong> — Sistema de Gestão da Qualidade (SaaS)
        conforme os módulos e condições descritos abaixo.
      </p>
    </div>

    <h2>Dados do Cliente</h2>
    <!-- Bloco com recuo próprio, como os quadros de cima: rente à borda, a
         primeira letra de cada rótulo saía cortada na impressão do ORC-2026-23
         ("NSTITUTO", "-MAIL", "º DE EMPRESAS"). -->
    <div style="padding:12px 16px;border:1px solid #e5e7eb;border-radius:8px;margin-bottom:8px;">
      <div class="campo"><span class="label">Cliente</span><div class="valor">${cliente?.nome || orc.cliente || '—'}</div></div>
      ${cnpjFormatado(cliente?.cnpj) ? `<div class="campo"><span class="label">CNPJ</span><div class="valor">${cnpjFormatado(cliente.cnpj)}</div></div>` : ''}
      ${cliente?.email ? `<div class="campo"><span class="label">E-mail</span><div class="valor">${cliente.email}</div></div>` : ''}
      <div class="campo" style="margin-bottom:0;"><span class="label">Nº de empresas contratadas</span><div class="valor" style="font-size:16px;font-weight:bold;color:#3b0764;">${numEmpresas} empresa${numEmpresas > 1 ? 's' : ''}</div></div>
    </div>

    <h2>Módulos e Submódulos Contratados</h2>
    <p style="font-size:12px;color:#666;margin:0 0 8px;">${totalSubmodulos} submódulo(s) em ${modulosComSelecao.length} módulo(s)</p>
    ${modulosComSelecao.length === 0
      ? '<p style="color:#999;font-style:italic;">Nenhum módulo selecionado.</p>'
      : `
      <table>
        <thead>
          <tr>
            <th>Módulo / Submódulo</th>
            <th style="text-align:right;width:110px;">Valor mensal<br><span style="font-weight:normal;font-size:10px;color:#888;">por empresa</span></th>
            <th style="text-align:right;width:70px;">Desconto</th>
            <th style="text-align:right;width:130px;">Subtotal mensal<br><span style="font-weight:normal;font-size:10px;color:#888;">${numEmpresas > 1 ? `× ${numEmpresas} empresas` : ''}</span></th>
          </tr>
        </thead>
        <tbody>${grupos}</tbody>
      </table>`}

    ${htmlExtras}

    <div style="background:#1e1b4b;border-radius:8px;padding:20px 24px;margin-top:24px;page-break-inside:avoid;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <span style="color:#c4b5fd;font-weight:bold;font-size:12px;text-transform:uppercase;letter-spacing:0.08em;">Investimento Mensal</span>
        <span style="color:#fff;font-weight:bold;font-size:26px;">R$ ${totalMensal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      </div>
      ${numEmpresas > 1 ? `
      <div style="display:flex;justify-content:space-between;align-items:center;padding-top:10px;border-top:1px solid #4338ca;margin-bottom:6px;">
        <span style="color:#a5b4fc;font-size:12px;">Por empresa (${numEmpresas} unidades)</span>
        <span style="color:#e0e7ff;font-weight:600;font-size:15px;">R$ ${totalPorEmpresa.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mês</span>
      </div>` : ''}
      ${totalExtrasAvulso > 0 ? `
      <div style="display:flex;justify-content:space-between;align-items:center;padding-top:10px;border-top:1px solid #4338ca;margin-bottom:6px;">
        <span style="color:#a5b4fc;font-size:12px;">+ Cobranças avulsas (uma vez)</span>
        <span style="color:#e0e7ff;font-weight:600;font-size:15px;">R$ ${totalExtrasAvulso.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      </div>` : ''}
      <div style="display:flex;justify-content:space-between;align-items:center;padding-top:10px;border-top:1px solid #4338ca;">
        <span style="color:#a5b4fc;font-size:12px;">Investimento anual estimado (12 meses${totalExtrasAvulso > 0 ? ' + avulsos' : ''})</span>
        <span style="color:#e0e7ff;font-weight:600;font-size:15px;">R$ ${totalAnual.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      </div>
    </div>

    <div style="border:1px solid #e5e7eb;border-radius:8px;padding:16px;margin-top:20px;page-break-inside:avoid;">
      <div style="font-size:11px;font-weight:bold;color:#888;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:8px;">Condições Comerciais</div>
      <ul style="margin:0;padding-left:18px;font-size:12px;color:#374151;line-height:1.7;">
        <li>Valores mensais. Faturamento com emissão de Nota Fiscal de Serviço.</li>
        <li>Ativação do ambiente e onboarding remoto inclusos na contratação.</li>
        <li>Reajuste anual pela variação positiva do IPCA/IBGE.</li>
        <li>Vigência mínima de 12 meses, com renovação automática.</li>
        ${orc.condicoesEspeciais ? `<li>${nl2br(orc.condicoesEspeciais)}</li>` : ''}
      </ul>
    </div>

    ${orc.observacoes ? `
    <div style="border:1px solid #fde68a;background:#fffbeb;border-radius:8px;padding:14px 16px;margin-top:20px;page-break-inside:avoid;">
      <div style="font-size:11px;font-weight:bold;color:#92400e;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:6px;">Observações</div>
      <p style="margin:0;font-size:13px;color:#78350f;">${nl2br(orc.observacoes)}</p>
    </div>` : ''}

    <div class="rodape">
      <div class="assinatura">
        <div class="linha-assinatura">Kzero Consultoria</div>
        <div style="font-size:11px;color:#888;margin-top:4px;">Emitente</div>
      </div>
      <div class="assinatura">
        <div class="linha-assinatura">${cliente?.nome || orc.cliente || 'Cliente'}</div>
        ${cnpjFormatado(cliente?.cnpj) ? `<div style="font-size:11px;color:#888;margin-top:4px;">CNPJ ${cnpjFormatado(cliente.cnpj)}</div>` : ''}
        <div style="font-size:11px;color:#888;margin-top:4px;">Ciente e de acordo</div>
      </div>
    </div>
  `;
};
